import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle } from "lucide-react";
import { shopProduct } from "../data/siteData";
import { useCart } from "../context/CartContext";

const ProductDetail = () => {
  const [seoOption, setSeoOption] = useState(shopProduct.seoOptions[0]);
  const [websiteOption, setWebsiteOption] = useState(shopProduct.websiteOptions[0]);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const { addItem } = useCart();
  const navigate = useNavigate();

  const price = useMemo(() => {
    const monthly = shopProduct.websiteMonthlyPrice[websiteOption];
    const multiplier = shopProduct.seoMultipliers[seoOption];
    return monthly * multiplier;
  }, [seoOption, websiteOption]);

  const billingLabel = shopProduct.seoBillingLabel[seoOption];
  const discountLabel = shopProduct.seoDiscountLabel[seoOption];

  const handleAddToCart = () => {
    addItem({
      cartId: `${shopProduct.slug}-${seoOption}-${websiteOption}-${Date.now()}`,
      id: shopProduct.slug,
      name: shopProduct.name,
      seoOption,
      websiteOption,
      price,
      quantity,
      image: shopProduct.image,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2500);
  };

  return (
    <div className="py-16 bg-white">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <nav className="text-xs text-p3-muted mb-8">
          <Link to="/shop" className="hover:text-p3-blue">Shop</Link> / {shopProduct.name}
        </nav>
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="bg-p3-cream rounded-2xl flex items-center justify-center p-16">
            <img src={shopProduct.image} alt={shopProduct.name} className="max-h-64 object-contain" />
          </div>
          <div>
            <p className="text-xs uppercase font-semibold text-p3-gold mb-2">{shopProduct.category}</p>
            <h1 className="font-heading font-extrabold text-3xl text-p3-navy mb-3">{shopProduct.name}</h1>
            <div className="flex items-center gap-3 mb-1">
              <p className="text-2xl font-bold text-p3-navy">${price.toFixed(2)}</p>
              {discountLabel && (
                <span className="text-xs font-semibold text-green-700 bg-green-50 px-2.5 py-1 rounded-full">
                  {discountLabel}
                </span>
              )}
            </div>
            {billingLabel && <p className="text-xs text-p3-muted mb-5">{billingLabel}</p>}
            {!billingLabel && <div className="mb-5" />}
            <p className="text-sm text-p3-muted leading-relaxed mb-8">{shopProduct.description}</p>

            <div className="mb-5">
              <label className="text-sm font-semibold text-p3-navy mb-2 block">SEO Optimization</label>
              <select
                value={seoOption}
                onChange={(e) => setSeoOption(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-p3-blue/40 text-sm bg-white"
              >
                {shopProduct.seoOptions.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>

            <div className="mb-5">
              <label className="text-sm font-semibold text-p3-navy mb-2 block">Websites</label>
              <select
                value={websiteOption}
                onChange={(e) => setWebsiteOption(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-p3-blue/40 text-sm bg-white"
              >
                {shopProduct.websiteOptions.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-4 mb-8">
              <label className="text-sm font-semibold text-p3-navy">Quantity</label>
              <input
                type="number"
                min={1}
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-20 px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-p3-blue/40 text-sm"
              />
            </div>

            <div className="flex flex-wrap gap-4">
              <button
                onClick={handleAddToCart}
                className="px-8 py-3.5 rounded-lg bg-p3-gold hover:bg-p3-gold/90 text-p3-navy font-semibold transition-all"
              >
                Add to Cart
              </button>
              <button
                onClick={() => navigate("/cart")}
                className="px-8 py-3.5 rounded-lg bg-p3-navy hover:bg-p3-navy-dark text-white font-semibold transition-all"
              >
                View Cart
              </button>
            </div>
            {added && (
              <p className="flex items-center gap-2 text-sm text-green-600 mt-4">
                <CheckCircle size={16} /> Added to cart!
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
